import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, SectionLabel, MaskedLine } from "@/components/Reveal";
import { EditorialMarquee, LogoNavy } from "@/components/Layout";
import { DIAG_URL } from "@/links";

const STEPS = [
  {
    id: "D1",
    t: "Diagnóstico",
    k: "Leitura",
    q: "Onde estamos de verdade? Como o meu cliente realmente pensa e decide?",
    d: "Mapeamos a situação atual, expomos incoerências entre discurso e entrega, analisamos a concorrência pela ótica da percepção e entendemos a jornada real do comprador.",
    axesTitle: "Cinco eixos de investigação",
    axes: [
      ["Negócio", "trajetória, modelo de receita, portfólio e ambições dos fundadores."],
      ["Marca", "proposta de valor, clareza da mensagem e ruídos de reputação."],
      ["Cliente", "critérios reais de contratação, objeções e recompra."],
      ["Mercado", "matriz de posicionamento competitivo contra concorrentes diretos e indiretos."],
      ["Marketing e Comunicação", "pontos de contato, funil comercial e canais digitais."],
    ],
    deliverables: [
      "Relatório Consolidado de Diagnóstico Estratégico",
      "Mapa de Posicionamento Atual e Matriz de Análise Concorrencial",
      "Síntese de evidências: preservar, corrigir, abandonar e acelerar",
    ],
    result: "Elimina iniciativas guiadas pelo gosto pessoal dos gestores e entrega aos sócios um espelho imparcial da tração do negócio.",
  },
  {
    id: "D2",
    t: "Direção",
    k: "Escolha",
    q: "Por que nos escolher, e para quem? Por que lembrar de você?",
    d: "Definimos as fronteiras de atuação da empresa: nichos de especialização, contra quem competir e quais perfis de cliente recusar. A proposta de valor é reconstruída para o nível real de consciência do mercado.",
    deliverables: [
      "Plataforma de Marca: proposta central de valor, mecanismo exclusivo de entrega e posicionamento competitivo",
      "Manifesto Institucional de Posicionamento",
      "Matriz de Voz e Território Verbal",
      "Arquitetura de Ofertas para simplificar o fechamento comercial",
    ],
    result: "Sem direção, qualquer esforço de design vira especulação visual. A liderança passa a delegar a comunicação sem receio de desvios.",
  },
  {
    id: "D3",
    t: "Desenho",
    k: "Expressão",
    q: "Como isso é percebido e lembrado?",
    d: "Transformamos a direção em um sistema visual e verbal que gera memória imediata e eleva a percepção de valor. A identidade é sinal de autoridade e diferenciação, não enfeite.",
    deliverables: [
      "Manual de Identidade Visual e Verbal",
      "Apresentação Comercial Institucional Master",
      "Modelos para redes sociais, propostas, papelaria digital e assinaturas",
      "Guia prático de execução para fornecedores externos",
    ],
    result: "Design ancorado em estratégia produz ativos de reconhecimento que facilitam a venda e criam barreira de entrada para a concorrência.",
  },
  {
    id: "D4",
    t: "Desdobramento",
    k: "Ação",
    q: "Como colocamos isso para funcionar? Como a marca começa a gerar resultado real?",
    d: "Estruturamos a implementação: governança de marketing, rituais de metas, fluxos comerciais de atração e indicadores de tração.",
    deliverables: [
      "Plano de Ativação e Distribuição de Canais",
      "Arquitetura da Jornada Comercial e Funil de Vendas",
      "Diretrizes de conteúdo e growth para 90 dias",
      "Painel Executivo de Governança de Marca e Indicadores",
      "Roadmap de implementação em 90 dias, 180 dias e 12 meses",
    ],
    result: "Converte planejamento em retorno de negócio e evita que a estratégia fique presa em documentos.",
  },
];

const GATEWAYS = [
  ["D1 Diagnóstico", "Todos", "Fica claro como o comprador processa valor e decide no seu setor."],
  ["D2 Direção", "Pessoal", "O decisor entende por que a sua empresa é indispensável ao cenário dele."],
  ["D3 Desenho", "Experiencial", "O comprador reconhece a marca na hora e associa alto valor a ela."],
  ["D4 Desdobramento", "Social", "O cliente valida a escolha diante dos pares e passa a recomendar a marca com precisão."],
];

const OUTCOMES = [
  ["Clareza executiva", "Um posicionamento em uma proposição inequívoca, que o time comercial articula com naturalidade."],
  ["Consistência de discurso", "Mesmo tom e conteúdo no site, nas apresentações, no WhatsApp e nas reuniões de fechamento."],
  ["Percepção de valor", "Autoridade que dá lastro técnico para defender preços superiores sem perder para concorrentes populares."],
  ["Demanda qualificada", "Canais filtrados pela mensagem certa e argumentos que desmontam objeções antes da proposta."],
  ["Recomendação espontânea", "Sua base de clientes entende para quem e como indicar o seu serviço."],
  ["Governança estratégica", "Rotinas trimestrais de acompanhamento baseadas em indicadores de percepção e pipeline."],
];

const OPTIONS = [
  {
    k: "Opção A",
    t: "Plataforma Completa",
    d: "As quatro etapas, D1 a D4, com a plataforma digital do cliente, relatórios apoiados por IA e acompanhamento do roadmap de ativação.",
    note: "Indicada para reposicionamento amplo, fusões, lançamento de divisões e marcas consolidadas que precisam de reestruturação profunda.",
  },
  {
    k: "Opção B",
    t: "Etapas únicas",
    d: "Entrada pelo diagnóstico e contratação modular. O D1 Diagnóstico Executivo leva de 10 a 15 dias. Depois, contrate a etapa que responde à sua necessidade mais urgente.",
    note: "Se avançar para a Plataforma Completa em até 30 dias após o relatório do D1, 100% do valor do D1 vira crédito.",
  },
  {
    k: "Opção C",
    t: "Conselho Estratégico de Marca",
    d: "Governança continuada depois do 4D: reuniões mensais de conselho, calibragem trimestral de rota, alinhamento de novos colaboradores comerciais e monitoramento de métricas.",
    note: "Indicada para quem quer supervisão sênior permanente, sem ampliar a estrutura fixa. Vigência mínima de 6 meses.",
  },
];

const StepBlock = ({ s, flip }) => (
  <div data-testid={`method-step-card-${s.id.toLowerCase()}`} className="grid gap-10 border-t border-white/10 py-20 lg:grid-cols-12 lg:py-28">
    <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
      <Reveal>
        <span className="font-display text-7xl font-extrabold text-outline-orange lg:text-8xl">{s.id}</span>
        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.3em] text-brand-magenta">{s.k}</p>
        <h3 className="mt-3 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">{s.t}</h3>
        <p className="mt-4 font-serif text-xl italic leading-relaxed text-slate-300">{s.q}</p>
        <p className="mt-5 text-sm leading-relaxed text-slate-400 sm:text-base">{s.d}</p>
      </Reveal>
    </div>
    <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
      {s.axes && (
        <Reveal className="mb-10">
          <h4 className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand-orange">{s.axesTitle}</h4>
          <ul className="mt-5 space-y-3">
            {s.axes.map(([k, v]) => (
              <li key={k} className="flex gap-3 border-b border-white/10 pb-3 text-sm leading-relaxed">
                <span className="font-semibold text-white">{k}:</span>
                <span className="text-slate-400">{v}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      )}
      <Reveal delay={0.1}>
        <h4 className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand-orange">Entregáveis</h4>
        <ul className="mt-5 space-y-3">
          {s.deliverables.map((item) => (
            <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-slate-300">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-orange" />
              {item}
            </li>
          ))}
        </ul>
        <p className="mt-8 border-l-2 border-brand-magenta pl-5 font-serif text-base italic leading-relaxed text-slate-300">
          {s.result}
        </p>
      </Reveal>
    </div>
  </div>
);

export default function Metodo4D() {
  return (
    <motion.main initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
      <section data-testid="method-hero" className="hero-glow noise relative overflow-hidden bg-ink pb-20 pt-36 lg:pb-28 lg:pt-48">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.15 }}>
            <SectionLabel>Método 4D</SectionLabel>
          </motion.div>
          <h1 className="mt-8 max-w-4xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.25}>Método, plataforma digital</MaskedLine>
            <MaskedLine delay={0.4}>
              e <span className="font-serif font-medium italic text-brand-orange">inteligência artificial.</span>
            </MaskedLine>
          </h1>
          <motion.p
            className="mt-8 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            Um ecossistema para posicionar, acelerar e mensurar marcas. Quatro etapas, cada uma com
            entregáveis definidos e a conclusão de uma servindo de base para a seguinte.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.85 }}>
            <Link
              to={DIAG_URL}
              data-testid="method-hero-cta"
              className="group mt-10 inline-flex items-center gap-3 bg-brand-orange px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-brand-red"
            >
              Fazer o Diagnóstico de Maturidade 4D
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </motion.div>
        </div>
      </section>

      <EditorialMarquee />

      <section data-testid="method-steps" className="bg-ink">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          {STEPS.map((s, i) => (
            <StepBlock key={s.id} s={s} flip={i % 2 === 1} />
          ))}
        </div>
      </section>

      <section data-testid="gateways-section" className="bg-paper py-24 text-navy lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <SectionLabel light>Gateways da mensagem</SectionLabel>
            <h2 className="mt-6 max-w-3xl font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              Os três gateways que toda mensagem precisa cruzar.
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-navy/70">
              O comprador de hoje filtra ruído com tecnologia e descarta discurso genérico.
              Cada etapa do método ativa um gateway (Kotler, Marketing 7.0).
            </p>
          </Reveal>
          <Reveal className="mt-14 overflow-x-auto">
            <table data-testid="gateways-table" className="w-full min-w-[640px] border-collapse text-left">
              <thead>
                <tr className="border-b-2 border-navy">
                  {["Etapa", "Gateway", "O que muda para o comprador"].map((h) => (
                    <th key={h} className="py-4 pr-6 font-mono text-[11px] uppercase tracking-[0.25em] text-navy/60">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {GATEWAYS.map(([e, g, d]) => (
                  <tr key={e} className="group border-b border-navy/10 transition-colors hover:bg-white">
                    <td className="py-5 pr-6 font-display text-lg font-bold">{e}</td>
                    <td className="py-5 pr-6">
                      <span className="border border-brand-orange/50 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-orange">
                        {g}
                      </span>
                    </td>
                    <td className="py-5 text-sm leading-relaxed text-navy/70">{d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
        </div>
      </section>

      <section data-testid="outcomes-section" className="bg-ink py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <SectionLabel>Resultado</SectionLabel>
            <h2 className="mt-6 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              O que muda na prática do negócio.
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {OUTCOMES.map(([t, d], i) => (
              <Reveal key={t} delay={(i % 3) * 0.08}>
                <div data-testid={`outcome-card-${i + 1}`} className="group h-full bg-ink p-8 transition-colors duration-500 hover:bg-navy">
                  <span className="font-mono text-xs text-brand-orange">0{i + 1}</span>
                  <h3 className="mt-4 font-display text-lg font-bold text-white transition-colors group-hover:text-brand-orange sm:text-xl">{t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="options-section" className="bg-paper py-24 text-navy lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <LogoNavy className="mb-8" />
            <SectionLabel light>Formatos de trabalho</SectionLabel>
            <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              Três formas de trabalhar com a Fuchs.
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-navy/70">
              Escolha o formato que combina com o momento da sua empresa.
              A proposta é calibrada depois do diagnóstico.
            </p>
          </Reveal>
          <div className="mt-16 grid gap-6 lg:grid-cols-3">
            {OPTIONS.map((o, i) => (
              <Reveal key={o.k} delay={i * 0.1}>
                <div
                  data-testid={`option-card-${["a", "b", "c"][i]}`}
                  className="group flex h-full flex-col border border-navy/15 bg-white p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-brand-orange lg:p-10"
                >
                  <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand-orange">{o.k}</span>
                  <h3 className="mt-4 font-display text-2xl font-bold">{o.t}</h3>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-navy/70">{o.d}</p>
                  <p className="mt-6 border-t border-navy/10 pt-5 font-serif text-sm italic leading-relaxed text-navy/55">{o.note}</p>
                  {i === 0 && (
                    <Link
                      to="/plataforma-4d"
                      data-testid="option-a-sales-link"
                      className="group mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-brand-orange transition-colors hover:text-navy"
                    >
                      Conhecer a página da Plataforma
                      <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14 border-l-2 border-brand-magenta bg-white p-8 lg:p-10">
            <p data-testid="honesty-note" className="max-w-3xl font-serif text-lg italic leading-relaxed text-navy/80">
              <span className="font-display not-italic font-bold text-navy">Nota de honestidade intelectual. </span>
              A Fuchs não vende atalhos de crescimento artificial. Entregamos método analítico,
              arquitetura de marca à prova de ruído e implementação controlada.
              O retorno financeiro vem dessa disciplina contínua.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="method-final-cta" className="noise relative overflow-hidden bg-ink py-28 lg:py-36">
        <div aria-hidden className="pointer-events-none absolute inset-0 hero-glow" />
        <div className="relative mx-auto max-w-4xl px-6 text-center lg:px-10">
          <Reveal>
            <h2 className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
              O ponto de partida é o{" "}
              <span className="font-serif font-medium italic text-brand-orange">diagnóstico.</span>
            </h2>
            <Link
              to={DIAG_URL}
              data-testid="method-final-cta-diagnostico"
              className="group mt-10 inline-flex items-center gap-3 bg-brand-orange px-10 py-5 font-mono text-xs uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-brand-red"
            >
              Fazer o Diagnóstico de Maturidade 4D
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </motion.main>
  );
}
